/**
 * Runtime schemas for the shared catalogue shapes in `./catalog`.
 *
 * The interfaces there are what consumers type against and they are NOT
 * replaced: every schema below carries `satisfies z.ZodType<Declared>`, so
 * adding a required field to a declared type, or giving a schema a type the
 * declaration disagrees with, fails `tsc` here. That check is one-directional --
 * a field a schema has and the interface lacks is legal, because these objects
 * are loose.
 *
 * WHAT "NOT STRICTER THAN THE WIRE" MEANS HERE.
 *  - Ids, SKUs, labels and free text are plain strings. Fixtures use ids like
 *    "p1", and the server does not promise a format a client could rely on.
 *  - A nullable column is `.nullish()`. The endpoints send whole database rows,
 *    so a missing description arrives as `null`, while the declared types say
 *    `description?: string`. `optionalText` turns the null into `undefined`,
 *    which makes the declared type true rather than widening it for every
 *    consumer.
 *  - Enums are closed only where the server's own set is closed: tier, status,
 *    image type.
 *  - Dates are strings. They are full ISO instants on the wire, including the
 *    ones documented as calendar dates.
 */

import { z } from "zod";
import {
	CATALOG_IMAGE_TYPES,
	type CatalogActivityEntry,
	type CatalogBrand,
	type CatalogCategory,
	type CatalogCategoryTreeNode,
	type CatalogDashboard,
	type CatalogProduct,
	type CatalogProductImage,
	type FieldChangeEntry,
	PRODUCT_TIERS,
	type ProductHistoryEntry,
	type ProductHistoryPage,
	type ProductPrice,
	type ProductVariant,
	type PublishBlocker,
	type SpecTemplate,
	type SpecTemplateAttribute,
	type SpecTemplateVariantAttribute,
} from "./catalog";
import { listOrEmpty, optionalText, recordOrEmpty } from "./wire";

export const catalogBrandSchema = z.looseObject({
	id: z.string(),
	name: z.string(),
	description: optionalText,
	website: optionalText,
	logoUrl: optionalText,
	isActive: z.boolean(),
	// Absent on the public reads (they project it away), `null` on a brand that
	// was never merged.
	mergedIntoId: z.string().nullish(),
}) satisfies z.ZodType<CatalogBrand>;

export const catalogCategorySchema = z.looseObject({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	description: optionalText,
	parentId: optionalText,
	displayOrder: z.number(),
	isActive: z.boolean(),
}) satisfies z.ZodType<CatalogCategory>;

export const catalogCategoryTreeNodeSchema: z.ZodType<CatalogCategoryTreeNode> =
	z.lazy(() =>
		catalogCategorySchema.extend({
			children: z.array(catalogCategoryTreeNodeSchema),
		}),
	);

export const catalogProductImageSchema = z.looseObject({
	url: z.string(),
	type: z.enum(CATALOG_IMAGE_TYPES).nullable(),
	alt: z.string().nullable(),
}) satisfies z.ZodType<CatalogProductImage>;

export const productVariantSchema = z.looseObject({
	id: z.string(),
	productId: z.string(),
	sku: optionalText,
	variantAttributes: recordOrEmpty(z.union([z.string(), z.number()])),
	images: listOrEmpty(catalogProductImageSchema),
	isActive: z.boolean(),
}) satisfies z.ZodType<ProductVariant>;

export const productPriceSchema = z.looseObject({
	amount: z.number(),
	uom: z.string(),
	basis: z.string(),
	basisLabel: z.string(),
	gstRateBp: z.number(),
	observedAt: z.string(),
	staleAfter: z.string(),
	isStale: z.boolean(),
	confidence: z.number(),
	observationId: z.string(),
	variantId: z.string(),
	city: z.string(),
}) satisfies z.ZodType<ProductPrice>;

export const specTemplateAttributeSchema = z.looseObject({
	id: z.string(),
	name: z.string(),
	attributeType: z.string(),
	isRequired: z.boolean(),
	displayOrder: z.number(),
	unit: z.string().nullish(),
	enumValues: z.array(z.string()).nullish(),
}) satisfies z.ZodType<SpecTemplateAttribute>;

export const specTemplateVariantAttributeSchema = z.looseObject({
	name: z.string(),
	type: z.string(),
	enumValues: z
		.array(z.string())
		.nullish()
		.transform((value) => value ?? undefined),
}) satisfies z.ZodType<SpecTemplateVariantAttribute>;

export const specTemplateSchema = z.looseObject({
	id: z.string(),
	categoryId: z.string(),
	variantAttributes: listOrEmpty(specTemplateVariantAttributeSchema),
	attributes: z.array(specTemplateAttributeSchema),
}) satisfies z.ZodType<SpecTemplate>;

export const catalogProductSchema = z.looseObject({
	id: z.string(),
	brandId: optionalText,
	categoryId: z.string(),
	name: z.string(),
	description: optionalText,
	sku: optionalText,
	tier: z.enum(PRODUCT_TIERS),
	status: z.enum(["draft", "active", "inactive"]),
	uom: z.string(),
	price: productPriceSchema.nullish(),
	prices: z.array(productPriceSchema).optional(),
	sourceable: z.boolean().optional(),
	unsourceableReason: z.string().nullish(),
	images: listOrEmpty(catalogProductImageSchema),
	specifications: recordOrEmpty(z.union([z.string(), z.number(), z.boolean()])),
	brand: catalogBrandSchema.optional(),
	category: catalogCategorySchema.optional(),
	variants: z.array(productVariantSchema).optional(),
	specTemplate: specTemplateSchema.optional(),
	createdAt: z.string(),
}) satisfies z.ZodType<CatalogProduct>;

export const publishBlockerSchema = z.looseObject({
	field: z.string(),
	message: z.string(),
	attributeName: z.string().optional(),
}) satisfies z.ZodType<PublishBlocker>;

const fieldChangeEntrySchema = z.looseObject({
	field: z.string(),
	from: z.unknown(),
	to: z.unknown(),
}) satisfies z.ZodType<FieldChangeEntry>;

export const productHistoryEntrySchema = z.looseObject({
	id: z.string(),
	action: z.string(),
	at: z.string(),
	actor: z.looseObject({ id: z.string().nullable(), label: z.string() }),
	changes: z.array(fieldChangeEntrySchema),
	detail: z.string().nullable(),
}) satisfies z.ZodType<ProductHistoryEntry>;

export const productHistoryPageSchema = z.looseObject({
	history: z.array(productHistoryEntrySchema),
	nextCursor: z.string().nullable().optional(),
}) satisfies z.ZodType<ProductHistoryPage>;

const catalogActivityEntrySchema = z.looseObject({
	id: z.string(),
	action: z.string(),
	at: z.string(),
	subjectType: z.string(),
	subjectId: z.string(),
	actor: z.string(),
}) satisfies z.ZodType<CatalogActivityEntry>;

export const catalogDashboardSchema = z.looseObject({
	products: z.looseObject({
		byStatus: z.looseObject({
			draft: z.number(),
			active: z.number(),
			inactive: z.number(),
		}),
		byVerification: z.record(z.string(), z.number()),
		total: z.number(),
	}),
	blockers: z.looseObject({
		scanned: z.number(),
		draftTotal: z.number(),
		blocked: z.number(),
		byField: z.looseObject({
			images: z.number(),
			hsnCode: z.number(),
			specifications: z.number(),
		}),
		capped: z.boolean(),
	}),
	stalePrices: z.looseObject({
		count: z.number(),
		oldest: z.string().nullable(),
		total: z.number(),
	}),
	recentActivity: z.array(catalogActivityEntrySchema),
}) satisfies z.ZodType<CatalogDashboard>;
